const JINA_BASE = 'https://r.jina.ai/'

const JUNK_PATTERNS = [
  /cookie/i, /consent/i, /privacy policy/i, /gdpr/i,
  /manage preference/i, /strictly necessary/i, /functional cookie/i,
  /performance cookie/i, /targeting cookie/i, /opt.out/i,
  /cookielaw/i, /onetrust/i, /we use cookies/i,
  /accept all/i, /reject all/i, /skip to (main|content|nav)/i,
  /^menu$/i, /^navigation$/i, /^sign in$/i, /^log in$/i,
  /^\s*\[.*\]\(https?:\/\/.*\)\s*$/,
]

const isJunk = (line) => JUNK_PATTERNS.some(p => p.test(line.trim()))
const stripJunk = (text) => text.split('\n').filter(l => !isJunk(l)).join('\n')

export class CookieWallError extends Error {
  constructor(title, company) {
    super('cookie_wall')
    this.title = title
    this.company = company
  }
}

export const scrapeJob = async (url) => {
  if (!url.startsWith('http')) {
    throw new Error('Please enter a full URL starting with https://')
  }

  const res = await fetch(`${JINA_BASE}${url}`, {
    headers: {
      Accept: 'text/plain',
      'X-Return-Format': 'text',
      'X-Remove-Selector': '#onetrust-banner-sdk, .optanon-alert-box-wrapper, #cookie-law-info-bar, .cookielaw, [class*="cookie-consent"], [id*="cookie-consent"]',
    },
  })

  if (!res.ok) throw new Error(`Could not fetch that URL (${res.status}). Try copying the full job posting URL.`)

  const rawText = await res.text()
  if (!rawText || rawText.length < 100) throw new Error('Page returned too little content. Try a different URL.')

  const cleaned = stripJunk(rawText)
  const rawLines = rawText.split('\n').filter(l => l.trim())
  const junkLines = rawLines.filter(l => isJunk(l))
  const junkRatio = junkLines.length / rawLines.length
  const hasCookieSignal = /manage consent|cookie preferences|onetrust|cookielaw|strictly necessary cookie/i.test(rawText.slice(0, 2000))

  if (hasCookieSignal || junkRatio > 0.4) {
    const title = extractTitle(cleaned, url)
    const company = extractCompany(cleaned, url)
    throw new CookieWallError(title, company)
  }

  const title = extractTitle(cleaned, url)
  const company = extractCompany(cleaned, url)
  const description = extractDescription(cleaned)
  return { url, title, company, description, rawText }
}

const extractTitle = (text, url) => {
  const patterns = [/^#+\s+(.{5,80})$/m, /title[:\s]+([^\n]{5,80})/i, /position[:\s]+([^\n]{5,80})/i, /^([A-Z][^\n]{10,60})\n/m]
  for (const p of patterns) {
    const m = text.match(p)
    if (m?.[1]) { const c = m[1].trim().replace(/[*_`#]/g, ''); if (c.length > 4) return c }
  }
  const slug = url.split('/').filter(s => s.length > 3 && !/^\d+$/.test(s)).pop() || ''
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Job Posting'
}

const extractCompany = (text, url) => {
  try {
    const parts = new URL(url).hostname.replace('www.', '').split('.')
    const name = parts[0]
    const genericHosts = ['lever','greenhouse','workday','taleo','icims','jobvite','smartrecruiters','myworkdayjobs','linkedin','indeed','glassdoor']
    if (!genericHosts.includes(name.toLowerCase())) return name.charAt(0).toUpperCase() + name.slice(1)
  } catch { /* fall through */ }
  const patterns = [/\bat\s+([A-Z][A-Za-z0-9&.,'-]{2,40})\b/, /company[:\s]+([^\n]{2,50})/i, /employer[:\s]+([^\n]{2,50})/i, /posted\s+by[:\s]+([^\n]{2,50})/i]
  for (const p of patterns) {
    const m = text.match(p)
    if (m?.[1]) return m[1].trim().replace(/[*_`]/g, '')
  }
  return ''
}

const extractDescription = (text) => {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0)
  const jobStartKeywords = [/about the role/i, /about this role/i, /the role/i, /job description/i, /responsibilities/i, /what you.ll do/i, /what you will do/i, /overview/i, /requirements/i, /qualifications/i, /we are looking/i, /we're looking/i, /you will/i, /your role/i, /position overview/i]
  let startIdx = lines.findIndex(l => jobStartKeywords.some(k => k.test(l)))
  if (startIdx < 0) startIdx = Math.min(15, lines.length - 1)
  const body = lines.slice(startIdx, startIdx + 150)
  const footerKeywords = /equal opportunity|eeo|disability|accommodation|apply now|submit application|share this/i
  const endIdx = body.findIndex(l => footerKeywords.test(l))
  return (endIdx > 10 ? body.slice(0, endIdx) : body).join('\n').trim()
}
